// import { useMemo } from 'react';

// export const useOffers = ({ subtotal, cartItems, couponCode }) => {
//   const appliedOffers = [];
//   let discountAmount = 0;

//   // 🏷️ Coupon Validation Logic
//   const validateCoupon = (code) => {
//     const normalizedCode = code.trim().toUpperCase();

//     switch (normalizedCode) {
//       case 'HELLOGLOW15':
//         discountAmount += subtotal * 0.15;
//         appliedOffers.push('15% OFF on First Order (HELLOGLOW15)');
//         return 'Coupon Applied: 15% OFF on First Order!';
//       case 'BUY2':
//         if (cartItems.length >= 2) {
//           discountAmount += subtotal * 0.10;
//           appliedOffers.push('10% OFF on 2+ Products (BUY2)');
//           return 'Coupon Applied: 10% OFF on 2+ Products!';
//         }
//         return 'Need at least 2 products for this offer.';
//       case 'BUY3':
//         if (cartItems.length >= 3) {
//           discountAmount += subtotal * 0.15;
//           appliedOffers.push('15% OFF on 3+ Products (BUY3)');
//           return 'Coupon Applied: 15% OFF on 3+ Products!';
//         }
//         return 'Need at least 3 products for this offer.';
//       case 'BUY5':
//         if (cartItems.length >= 5) {
//           discountAmount += subtotal * 0.20;
//           appliedOffers.push('20% OFF on 5+ Products (BUY5)');
//           return 'Coupon Applied: 20% OFF on 5+ Products!';
//         }
//         return 'Need at least 5 products for this offer.';
//       case 'BOGO':
//         if (cartItems.length >= 2) {
//           const cheapest = Math.min(...cartItems.map((item) => item.price));
//           discountAmount += cheapest;
//           appliedOffers.push(`BOGO Deal Applied: ₹${cheapest.toFixed(2)} OFF (Cheapest Free)`);
//           return `Coupon Applied: BOGO - ₹${cheapest.toFixed(2)} OFF!`;
//         }
//         return 'Need at least 2 products for BOGO deal.';
//       default:
//         return 'Invalid Coupon Code';
//     }
//   };

//   // Auto-apply if coupon entered
//   const autoApply = validateCoupon(couponCode);

//   // 🏷️ Extra 5% discount on online payment
//   const applyOnlineDiscount = (amount) => {
//     const discount = amount * 0.05;
//     return {
//       extraDiscount: discount,
//       newTotal: amount - discount,
//     };
//   };

//   // Calculate Final Total
//   const finalTotal = useMemo(() => {
//     const totalAfterDiscount = subtotal - discountAmount;
//     return totalAfterDiscount > 0 ? totalAfterDiscount : 0;
//   }, [subtotal, discountAmount]);

//   return {
//     appliedOffers,
//     discountAmount,
//     finalTotal,
//     validateCoupon,
//     applyOnlineDiscount,
//   };
// };
import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

export const useOffers = ({ subtotal, cartItems, couponCode }) => {
  const [offers, setOffers] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [appliedOffers, setAppliedOffers] = useState([]);
  const [couponMessage, setCouponMessage] = useState('');

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'offers'));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const activeOffers = data.filter(offer => offer.enabled);
        setOffers(activeOffers);
      } catch (error) {
        console.error('Error fetching offers:', error);
      }
    };

    fetchOffers();
  }, []);

  const validateCoupon = (code) => {
    const normalizedCode = code.trim().toUpperCase();
    const matchedOffer = offers.find(offer => offer.code.toUpperCase() === normalizedCode);

    if (!matchedOffer) {
      setCouponMessage('Invalid Coupon Code');
      return 'Invalid Coupon Code';
    }

    // Total product quantity count (includes same product multiple times)
    const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    if (matchedOffer.minProducts && totalQuantity < matchedOffer.minProducts) {
      const msg = `Need at least ${matchedOffer.minProducts} products for this offer.`;
      setCouponMessage(msg);
      return msg;
    }

    let discount = 0;
    let message = '';

    if (matchedOffer.type === 'percentage') {
      discount = subtotal * (matchedOffer.discount / 100);
      message = `Coupon Applied: ${matchedOffer.discount}% OFF!`;
    } else if (matchedOffer.type === 'fixed') {
      discount = matchedOffer.discount;
      message = `Coupon Applied: ₹${matchedOffer.discount} OFF!`;
    } else if (matchedOffer.type === 'bogo') {
      // Expand items by quantity for BOGO
      const expandedItems = [];
      cartItems.forEach(item => {
        for (let i = 0; i < item.quantity; i++) {
          expandedItems.push({ ...item, quantity: 1 });
        }
      });

      const sortedItems = expandedItems.sort((a, b) => a.price - b.price);
      const freeItems = matchedOffer.maxFreeItems > 0
        ? Math.min(matchedOffer.maxFreeItems, Math.floor(expandedItems.length / 2))
        : Math.floor(expandedItems.length / 2);
      const discountItems = sortedItems.slice(0, freeItems);
      const discountTotal = discountItems.reduce((sum, item) => sum + item.price, 0);
      discount = discountTotal;
      message = `Coupon Applied: BOGO - ₹${discountTotal.toFixed(2)} OFF! (${freeItems} Free Item${freeItems > 1 ? 's' : ''})`;
    }

    setDiscountAmount(discount);
    setAppliedOffers([matchedOffer.title]);
    setCouponMessage(message);
    return message;
  };

  useEffect(() => {
    if (couponCode) {
      validateCoupon(couponCode);
    } else {
      setDiscountAmount(0);
      setAppliedOffers([]);
      setCouponMessage('');
    }
  }, [couponCode, offers, subtotal, cartItems]);

  const applyOnlineDiscount = (amount) => {
    const discount = amount * 0.05;
    return {
      extraDiscount: discount,
      newTotal: amount - discount,
    };
  };

  const finalTotal = useMemo(() => {
    const totalAfterDiscount = subtotal - discountAmount;
    return totalAfterDiscount > 0 ? totalAfterDiscount : 0;
  }, [subtotal, discountAmount]);

  return {
    offers,
    appliedOffers,
    discountAmount,
    finalTotal,
    validateCoupon,
    couponMessage,
    applyOnlineDiscount,
  };
};
